import transportImg from "assets/img/transport.svg"

import { FC, ReactNode, useEffect, useMemo, useState } from "react"
import { useTranslation, Trans } from "react-i18next"
import { Link } from "react-router-dom"
import cx from "classnames"

import Reservation from "models/Reservation"
import Text from "components/Text/Text"
import Button from "components/Button/Button"
import Spinner from "components/Spinner/Spinner"
import Routes from "@/routes"
import { Flex } from "components/Flex"
import { ReservationStep } from "pages/ReservationPage/config"
import useReservationShippingOrders from "hooks/useReservationShippingOrders"
import { ShippingOrderStatus, ShippingOrderType } from "models/ShippingOrder"
import ShippingOrderPending from "components/ShippingOrder/ShippingOrderPending"
import ShippingOrderEditModal from "components/ShippingOrderModal/ShippingOrderEditModal"
import { useTenantSettings } from "stores/tenantSettings"
import toastMessages from "stores/toastMessages"
import ShippingOrderSentToStockCoordinator from "components/ShippingOrder/ShippingOrderSentToStockCoordinator"
import { InfoDropdown, InfoDropdownList, InfoDropdownListItemButton } from "components/InfoDropdown"
import { IInfoDropdownProps } from "components/InfoDropdown/InfoDropdown"
import { UserPermission } from "config/constants"
import { usePermissions } from "stores/permissions"

interface IReservationSidebarShippingProps {
    reservation: Reservation
    onShippingOrderChange(): Promise<void>
    canEditReservation: boolean
}

enum ReservationSidebarShippingStatus {
    loading = "loading",
    notReadyForShipping = "notReadyForShipping",
    noShippingOrders = "noShippingOrders",
    shippingOrders = "shippingOrders",
}

const ReservationSidebarShipping: FC<IReservationSidebarShippingProps> = ({
    reservation,
    onShippingOrderChange,
    canEditReservation,
}) => {
    const { t } = useTranslation()
    const { hasPermission } = usePermissions()
    const [status, setStatus] = useState<ReservationSidebarShippingStatus>(ReservationSidebarShippingStatus.loading)
    const [shippingOrderTypeToCreate, setShippingOrderTypeToCreate] = useState<ShippingOrderType | null>(null)
    const { deliveryTerms, fetchDeliveryTerms, tenantWarehouses, fetchTenantWarehouses } = useTenantSettings()

    const { shippingOrders, isLoadingShippingOrders } = useReservationShippingOrders({
        linkGetShippingOrders: reservation.linkGetShippingOrders
    })

    const shippingOrdersPending = useMemo(() => {
        return shippingOrders?.filter(shippingOrder => {
            return shippingOrder.status === ShippingOrderStatus.pending
                || shippingOrder.status === ShippingOrderStatus.forwardedToShipping
        }) || []
    }, [shippingOrders])

    const shippingOrdersSentToStockCoordinator = useMemo(() => {
        return shippingOrders?.filter(shippingOrder => {
            return shippingOrder.status === ShippingOrderStatus.pushedToStock
                || shippingOrder.status === ShippingOrderStatus.inProgress
                || shippingOrder.status === ShippingOrderStatus.completed
        }) || []
    }, [shippingOrders])

    useEffect(() => {
        const fetchTerms = async () => {
            try {
                await fetchDeliveryTerms()
            } catch (e) {
                toastMessages.showSystemError()
            }
        }
        fetchTerms()
    }, [fetchDeliveryTerms])

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                await fetchTenantWarehouses()
            } catch (e) {
                toastMessages.showSystemError()
            }
        }
        fetchWarehouses()
    }, [fetchTenantWarehouses])

    useEffect(() => {
        if (!reservation.readyForShipping) {
            setStatus(ReservationSidebarShippingStatus.notReadyForShipping)
            return
        }

        if (isLoadingShippingOrders) {
            setStatus(ReservationSidebarShippingStatus.loading)
            return
        }

        if (shippingOrders && shippingOrders.length === 0) {
            setStatus(ReservationSidebarShippingStatus.noShippingOrders)
            return
        }

        setStatus(ReservationSidebarShippingStatus.shippingOrders)
    }, [isLoadingShippingOrders, reservation.readyForShipping, reservation.time.reservationStart, shippingOrders])

    function getAddShippingOrderDropdown({ button, placement }: { button: ReactNode, placement: IInfoDropdownProps["placement"] }) {
        return <InfoDropdown
            label={button}
            placement={placement}
            content={closeDropdown => (
                <InfoDropdownList>
                    <InfoDropdownListItemButton
                        onClick={() => {
                            setShippingOrderTypeToCreate(ShippingOrderType.delivery)
                            closeDropdown()
                        }}>
                        {t("shippingOrderType.delivery")}
                    </InfoDropdownListItemButton>
                    <InfoDropdownListItemButton
                        onClick={() => {
                            setShippingOrderTypeToCreate(ShippingOrderType.return)
                            closeDropdown()
                        }}>
                        {t("shippingOrderType.return")}
                    </InfoDropdownListItemButton>
                </InfoDropdownList>
            )}
        />
    }

    const canShowAddShippingOrderButton = hasPermission(UserPermission.ShippingOrdersCreate)
        && (status === ReservationSidebarShippingStatus.shippingOrders || status === ReservationSidebarShippingStatus.noShippingOrders)

    return (
        <>
            <Flex alignItems="baseline" justifyContent="space-between">
                <Text type="h3" className="mb-6">{t("shippingOrders.shippingOrders")}</Text>
                {canShowAddShippingOrderButton && (shippingOrdersPending.length > 0 || shippingOrdersSentToStockCoordinator.length > 0) &&
                    getAddShippingOrderDropdown({
                        button: <Button
                            theme="secondary"
                            label={t('shippingOrders.addShippingOrderBtn')}
                        />,
                        placement: "bottom-end"
                    })
                }
            </Flex>
            {status === ReservationSidebarShippingStatus.shippingOrders &&
                <>
                    {shippingOrdersPending.length > 0 &&
                        <>
                            <Text type="pSmallest" className="color-grey-600 font-bold mb-2">
                                {t('shippingOrders.pending')}
                            </Text>
                            {shippingOrdersPending.map((shippingOrder, idx) => (
                                <div className="mb-2" key={idx}>
                                    <ShippingOrderPending
                                        shippingOrder={shippingOrder}
                                        reservation={reservation}
                                        deliveryTerms={deliveryTerms?.entities}
                                        tenantWarehouses={tenantWarehouses?.entities}
                                        onChanged={onShippingOrderChange}
                                    />
                                </div>
                            ))}
                        </>
                    }
                    {shippingOrdersSentToStockCoordinator.length > 0 &&
                        <>
                            <Text type="pSmallest" className={cx([
                                "color-grey-600 font-bold mb-2",
                                { "mt-6": shippingOrdersPending.length > 0 }
                            ])}>
                                {t('shippingOrders.sentToStockCoordinator')}
                            </Text>
                            {shippingOrdersSentToStockCoordinator.map((shippingOrder, idx) => (
                                <div className="mb-2" key={idx}>
                                    <ShippingOrderSentToStockCoordinator
                                        shippingOrder={shippingOrder}
                                        reservation={reservation}
                                    />
                                </div>
                            ))}
                        </>
                    }
                </>
            }
            {status === ReservationSidebarShippingStatus.noShippingOrders &&
                <Flex flow="column" alignItems="center" className="text-center py-8">
                    <img src={transportImg} alt="" className="mb-4" />
                    <Text className="white-space-pre-line mb-4">
                        {t(canShowAddShippingOrderButton
                            ? "shippingOrders.noShippingOrdersCreate"
                            : "shippingOrders.noShippingOrders"
                        )}
                    </Text>
                    {canShowAddShippingOrderButton &&
                        getAddShippingOrderDropdown({
                            button: <Button
                                label={t('shippingOrders.addShippingOrderBtn')}
                            />,
                            placement: "bottom"
                        })
                    }
                </Flex>
            }
            {status === ReservationSidebarShippingStatus.notReadyForShipping &&
                <div className="p-4 text-center">
                    {t('shippingOrders.requestNotReadyForShipping')}
                    {canEditReservation &&
                        <Trans i18nKey="shippingOrders.requestNotReadyForShippingShippingDetails">
                            Tick the checkbox “Ready for shipping” on the
                            <Link to={Routes.reservationEdit.getPath({ reservationId: reservation.id }, { step: ReservationStep.delivery })} className="link color-brand-blue">delivery</Link>
                            in order to create a shipping order.
                        </Trans>
                    }
                </div>
            }
            {status === ReservationSidebarShippingStatus.loading &&
                <Spinner className="p-8" />
            }
            {!!shippingOrderTypeToCreate &&
                <ShippingOrderEditModal
                    reservation={reservation}
                    type={shippingOrderTypeToCreate}
                    onClose={() => setShippingOrderTypeToCreate(null)}
                    onSaved={onShippingOrderChange}
                />
            }
        </>
    )
}

export default ReservationSidebarShipping
